import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { NotificationSnackbarComponent } from '@shared-components/notification-snackbar/notification-snackbar.component';
import { CustomModalComponent } from '@shared-components/custom-modal/custom-modal.component';
import { ApiErrorAdapter } from './api-error-adapter.service';
import { isSubscriptionRequiredError } from '../subscription-required';

export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private apiErrors: ApiErrorAdapter,
  ) {}

  showTotast(message: string): void {
    this.snackBar.openFromComponent(NotificationSnackbarComponent, {
      data: {
        title: 'Realizado!',
        message,
        type: NotificationType.SUCCESS,
      },
      duration: 4000,
      panelClass: [`snack-${NotificationType.SUCCESS}`],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  showToastError(error: unknown, fallbackMessage?: string): void {
    // The 402 interceptor already shows its own dialog with "Suscribirme".
    if (isSubscriptionRequiredError(error)) return;

    const message = this.apiErrors.from(error, fallbackMessage).message;

    this.snackBar.openFromComponent(NotificationSnackbarComponent, {
      data: {
        title: 'Ups!',
        message,
        type: NotificationType.ERROR,
      },
      duration: 5000,
      panelClass: [`snack-${NotificationType.ERROR}`],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  /**
   * Non-blocking advisory (rol semántico "Advertencia", ámbar — nunca
   * bloquea la acción que la generó). Mismo criterio ya usado para el
   * margen negativo en `product-row` (`design-system.md`, "Switch margen
   * ↔ precio de venta"), acá aplicado vía snackbar en vez de un hint
   * inline porque la condición se conoce recién en la respuesta del
   * backend (ej. confirmar una orden con costo tercerizado por encima del
   * precio de venta).
   */
  showToastWarning(message: string, title = 'Atención'): void {
    this.snackBar.openFromComponent(NotificationSnackbarComponent, {
      data: {
        title,
        message,
        type: NotificationType.WARNING,
      },
      duration: 6000,
      panelClass: [`snack-${NotificationType.WARNING}`],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  showSuccess(message: string, title = 'Realizado'): void {
    this.matDialog.open(CustomModalComponent, {
      data: {
        title,
        message,
        rejectText: 'Cancelar',
        acceptText: 'Aceptar',
      },
    });
  }
}
